import { Check } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

export type ListingStepId = 'token' | 'organisation' | 'resources';

export type ListingStepState = 'pending' | 'active' | 'done';

export type ListingStep = {
  id: ListingStepId;
  title: string;
  detail: string;
  duration: string;
  state: ListingStepState;
};

type AddRepositoryListingProgressProps = {
  heading: string;
  subheading: string;
  steps: ListingStep[];
  error?: string | null;
};

const AddRepositoryListingProgress = ({
  heading,
  subheading,
  steps,
  error,
}: AddRepositoryListingProgressProps) => (
  <div className="mt-6">
    <h2 className="m-0 mb-1 text-[0.9375rem] font-semibold leading-snug text-[var(--inv-text)]">
      {heading}
    </h2>
    <p className="mb-4 m-0 text-[0.8125rem] text-[var(--inv-text-muted)]">
      {subheading}
    </p>

    <div className="overflow-hidden rounded-[12px] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)]">
      {steps.map((step, index) => (
        <div
          key={step.id}
          className={cn(
            'flex items-start gap-3 px-4 py-3.5',
            index > 0 &&
              'border-0 border-t border-solid border-[var(--inv-border-soft)]',
          )}>
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
            {step.state === 'done' ? (
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#12b76a] text-white">
                <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />
              </span>
            ) : step.state === 'active' ? (
              <Spinner compact />
            ) : (
              <span className="h-5 w-5 rounded-full border border-solid border-[var(--inv-border)] bg-[var(--inv-surface-muted)]" />
            )}
          </span>
          <div className="min-w-0 flex-1">
            <p className="m-0 text-sm font-semibold text-[var(--inv-text)]">
              {step.title}
            </p>
            {step.detail ? (
              <p className="m-0 mt-0.5 text-[0.8125rem] text-[var(--inv-text-muted)]">
                {step.detail}
              </p>
            ) : null}
          </div>
          {step.duration ? (
            <span className="shrink-0 pt-0.5 text-[0.75rem] text-[var(--inv-text-muted)]">
              {step.duration}
            </span>
          ) : null}
        </div>
      ))}
    </div>

    {error ? (
      <p className="mt-2 m-0 text-[0.8125rem] text-[#b42318]" role="alert">
        {error}
      </p>
    ) : null}
  </div>
);

export default AddRepositoryListingProgress;
